import { useState, type ComponentProps, type KeyboardEvent, type Ref } from 'react';
import { PASSWORD } from '@locales/id/auth';
import { Icon } from './Icon';
import type { IconName } from './icons';

type Props = Omit<ComponentProps<'input'>, 'type' | 'ref'> & {
  /** leading icon inside the field (the login's lock) */
  icon?: IconName;
  inputRef?: Ref<HTMLInputElement>;
  /** Caps Lock turned on or off while typing here (show a hint next to the field) */
  onCapsLock?: (on: boolean) => void;
};

/**
 * Password field with the show / hide eye inside its right edge (the tap area is the field's full height) and
 * Caps Lock detection. The eye is a toggle button (aria-pressed), never submits the form.
 */
export function PasswordInput({ icon, inputRef, onCapsLock, onKeyDown, onKeyUp, className = '', ...rest }: Props) {
  const [show, setShow] = useState(false);
  const caps = (e: KeyboardEvent<HTMLInputElement>) => onCapsLock?.(e.getModifierState?.('CapsLock') ?? false);
  return (
    <div className={`pw-field ${icon ? 'input-icon' : ''} ${className}`}>
      {icon && <Icon name={icon} />}
      <input
        ref={inputRef}
        type={show ? 'text' : 'password'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        onKeyDown={(e) => {
          caps(e);
          onKeyDown?.(e);
        }}
        onKeyUp={(e) => {
          caps(e);
          onKeyUp?.(e);
        }}
        {...rest}
      />
      <button type="button" className="pw-toggle" onClick={() => setShow((s) => !s)} aria-label={show ? PASSWORD.hide : PASSWORD.show} aria-pressed={show} aria-controls={rest.id}>
        {/* keyed: the new eye pops in when the state flips */}
        <span key={show ? 'shown' : 'hidden'} className="pw-eye">
          <Icon name={show ? 'eye-off' : 'eye'} size={18} />
        </span>
      </button>
    </div>
  );
}
